import { Link, useSearch } from "@tanstack/react-router";
import { ShieldCheck, ArrowLeft, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "@/shared/components";
import { Numeric } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { AuthTemplate } from "@modootoday/ui-widgets/templates";

const DASHBOARD_HOME_PATH = "/dashboard";

export function VerifyEmailPage() {
  const search = useSearch({ from: "/auth/verify-email" }) as {
    email?: string;
  };
  const email = search.email || "";

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const handleChange = (idx: number, val: string) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const next = [...code];
    next[idx] = val;
    setCode(next);
    if (val && idx < 5) {
      const nextInput = document.getElementById(`vc-${idx + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      const prevInput = document.getElementById(`vc-${idx - 1}`);
      prevInput?.focus();
    }
  };

  const fullCode = code.join("");
  const canSubmit = fullCode.length === 6 && email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: fullCode,
        type: "signup",
      });

      if (error) throw error;

      setVerified(true);
      toast.success("이메일 인증이 완료되었습니다");
    } catch (err: any) {
      toast.error(err.message || "인증에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("이메일 정보가 없습니다");
      return;
    }
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
      });
      if (error) throw error;
      toast.success("인증 코드가 재발송되었습니다");
    } catch (err: any) {
      toast.error(err.message || "재발송에 실패했습니다");
    }
  };

  if (verified) {
    return (
      <AuthTemplate
        title="인증 완료"
        subtitle="이메일 인증이 완료되었습니다. 이제 모든 기능을 이용하실 수 있습니다."
      >
        <div className="flex justify-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-signal-success/10">
            <ShieldCheck className="size-5 text-signal-success" />
          </div>
        </div>
        <Link
          to={DASHBOARD_HOME_PATH as string}
          className="mt-2 inline-flex items-center justify-center gap-2 rounded-sm bg-accent px-5 py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90"
        >
          대시보드로 이동
        </Link>
      </AuthTemplate>
    );
  }

  return (
    <AuthTemplate
      title="이메일 인증"
      subtitle="이메일로 전송된 6자리 인증 코드를 입력하세요."
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Numeric className="flex justify-center gap-2">
          {code.map((digit, i) => (
            <input
              key={i}
              id={`vc-${i}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="size-11 rounded-lg border border-stroke-default bg-surface-base text-center text-[18px] font-bold outline-none ring-stroke-focus transition-shadow focus:ring-2"
            />
          ))}
        </Numeric>

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <span className="size-4 animate-spin rounded-full border-2 border-text-inverse/30 border-t-primary-foreground" />
          ) : (
            <ShieldCheck className="size-4" />
          )}
          {loading ? "확인 중..." : "인증 확인"}
        </button>
      </form>

      <div className="mt-2 flex items-center justify-between text-[12px] text-text-tertiary">
        <Link
          to="/auth/login"
          className="inline-flex items-center gap-1 font-medium hover:text-text-primary"
        >
          <ArrowLeft className="size-3" /> 로그인
        </Link>
        <button
          type="button"
          onClick={handleResend}
          className="inline-flex items-center gap-1 font-medium hover:text-text-primary"
        >
          <RefreshCw className="size-3" /> 코드 재발송
        </button>
      </div>
    </AuthTemplate>
  );
}
